/**
 * PMV formal commercial / field printables via shared print-engine.
 * Money always from backend payloads — missing rate never shown as 0.
 */
import React from 'react';
import dayjs from 'dayjs';
import { EngineCommercialA4Print } from '@/components/print/EngineCommercialPrints';
import { openPmvPrintOverlay } from '@/utils/openPmvPrintOverlay';
import {
  formatHireMoney,
  type PmvHireReconciliation,
  type PmvHireStatement,
  type PmvHireTimesheet,
} from '@/services/pmv-hire.service';
import type { PmvDailyLogSheetDetail, PmvDailyLogSheetEntry } from '@/services/pmv-field-ops.service';

function fmtDate(v?: string | null): string {
  if (!v) return '';
  const d = dayjs(v);
  return d.isValid() ? d.format('DD MMM YYYY') : String(v);
}

type Labels = {
  agreementLabel?: string;
  supplierLabel?: string;
  equipmentLabel?: string;
  projectLabel?: string;
};

export type HireTimesheetPrintContext = Labels & {
  timesheet: PmvHireTimesheet;
};

export type HireStatementPrintContext = Labels & {
  statement: PmvHireStatement;
  timesheetLabel?: string;
};

export type HireReconPrintContext = Labels & {
  recon: PmvHireReconciliation;
  statementLabel?: string;
  currency?: string;
};

export type DailyLogSheetPrintContext = {
  sheet: PmvDailyLogSheetDetail;
  entries: PmvDailyLogSheetEntry[];
  projectLabel?: string;
};

export const PrintableHireTimesheet: React.FC<
  HireTimesheetPrintContext & { onClose?: () => void }
> = ({ timesheet: ts, agreementLabel, supplierLabel, equipmentLabel, onClose }) => {
  const lines = (ts.lines ?? []).map((row, i) => {
    const r = row as Record<string, unknown>;
    return {
      lineNumber: i + 1,
      description: String(r.explanation || r.operating_date || `Line ${i + 1}`),
      cells: {
        date: String(r.operating_date || '—'),
        work_min: r.working_minutes == null ? '—' : String(r.working_minutes),
        billable: r.billable_quantity == null ? '—' : String(r.billable_quantity),
        amount: formatHireMoney(
          r.line_amount as string | number | null | undefined,
          ts.currency
        ),
      },
    };
  });
  if (!lines.length) {
    lines.push({
      lineNumber: 1,
      description: 'No timesheet lines',
      cells: { date: '—', work_min: '—', billable: '—', amount: '—' },
    });
  }

  return (
    <EngineCommercialA4Print
      docType="pmv_hire_timesheet"
      title="EQUIPMENT HIRE TIMESHEET"
      documentNumber={ts.timesheet_number || 'HTS'}
      partyName={supplierLabel || 'Supplier'}
      letterhead
      onClose={onClose}
      introText={
        'Commercial timesheet derived from hire calculation against verified Daily Logs. ' +
        'Working minutes ≠ billable quantity. Amounts are backend-calculated — missing rate is Unavailable, never 0. ' +
        'Does not create Supplier Invoice, AP or GL.'
      }
      metaLines={[
        { label: 'Timesheet No', value: ts.timesheet_number || '' },
        { label: 'Status', value: String(ts.status || '') },
        { label: 'Agreement', value: agreementLabel || ts.hire_agreement_id || '' },
        { label: 'Equipment', value: equipmentLabel || '' },
        { label: 'Supplier', value: supplierLabel || '' },
        {
          label: 'Period',
          value: `${fmtDate(ts.period_from)} → ${fmtDate(ts.period_to)}`,
        },
        { label: 'Total', value: formatHireMoney(ts.total_amount, ts.currency) },
        { label: 'Currency', value: ts.currency || '' },
      ].filter((m) => m.value)}
      columns={[
        { key: 'date', label: 'Date' },
        { key: 'work_min', label: 'Work min' },
        { key: 'billable', label: 'Billable' },
        { key: 'amount', label: 'Amount', align: 'right' },
      ]}
      lines={lines}
      finals={[{ id: 'ts-note', heightPx: 56 }]}
      finalBlocks={{
        'ts-note': (
          <div style={{ fontSize: 10, color: '#444' }}>
            Certified timesheets may feed hire statements. Notes: {ts.notes || '—'}
          </div>
        ),
      }}
    />
  );
};

export const PrintableHireStatement: React.FC<
  HireStatementPrintContext & { onClose?: () => void }
> = ({
  statement: s,
  agreementLabel,
  supplierLabel,
  equipmentLabel,
  timesheetLabel,
  onClose,
}) => {
  const lines = (s.lines ?? []).map((row, i) => {
    const r = row as Record<string, unknown>;
    return {
      lineNumber: i + 1,
      description: String(r.description || `Line ${i + 1}`),
      cells: {
        qty: r.quantity == null ? '—' : String(r.quantity),
        amount: formatHireMoney(
          r.line_amount as string | number | null | undefined,
          s.currency
        ),
      },
    };
  });
  if (!lines.length) {
    lines.push({
      lineNumber: 1,
      description: 'No statement lines',
      cells: { qty: '—', amount: '—' },
    });
  }

  return (
    <EngineCommercialA4Print
      docType="pmv_hire_statement"
      title="EQUIPMENT HIRE STATEMENT"
      documentNumber={s.statement_number || 'HST'}
      partyName={supplierLabel || 'Supplier'}
      letterhead
      onClose={onClose}
      introText={
        'Immutable commercial snapshot of hire charges for the stated period. ' +
        'Expected amount for vendor invoice reconciliation. ' +
        'Does not replace Supplier Invoice, AP payable, or GL posting authority.'
      }
      metaLines={[
        { label: 'Statement No', value: s.statement_number || '' },
        { label: 'Status', value: String(s.status || '') },
        { label: 'Agreement', value: agreementLabel || s.hire_agreement_id || '' },
        { label: 'Timesheet', value: timesheetLabel || s.timesheet_id || '' },
        { label: 'Equipment', value: equipmentLabel || '' },
        { label: 'Supplier', value: supplierLabel || '' },
        { label: 'Total', value: formatHireMoney(s.total_amount, s.currency) },
        { label: 'Currency', value: s.currency || '' },
      ].filter((m) => m.value)}
      columns={[
        { key: 'description', label: 'Description' },
        { key: 'qty', label: 'Qty' },
        { key: 'amount', label: 'Amount', align: 'right' },
      ]}
      lines={lines}
      finals={[{ id: 'st-note', heightPx: 56 }]}
      finalBlocks={{
        'st-note': (
          <div style={{ fontSize: 10, color: '#444' }}>
            Certified statements must not be rewritten by later PO rate amendments. Use Invoice
            Reconciliation for variance vs vendor invoice.
          </div>
        ),
      }}
    />
  );
};

export const PrintableHireReconciliation: React.FC<
  HireReconPrintContext & { onClose?: () => void }
> = ({
  recon: r,
  agreementLabel,
  supplierLabel,
  equipmentLabel,
  statementLabel,
  currency,
  onClose,
}) => {
  const cur = currency;
  const lines = (r.lines ?? []).map((row, i) => {
    const line = row as Record<string, unknown>;
    return {
      lineNumber: i + 1,
      description: String(line.description || `Line ${i + 1}`),
      cells: {
        expected: formatHireMoney(
          line.expected_amount as string | number | null | undefined,
          cur
        ),
        invoiced: formatHireMoney(
          line.invoiced_amount as string | number | null | undefined,
          cur
        ),
        variance: formatHireMoney(
          line.variance_amount as string | number | null | undefined,
          cur
        ),
      },
    };
  });
  if (!lines.length) {
    lines.push({
      lineNumber: 1,
      description: 'Header-level variance only (no line split)',
      cells: {
        expected: formatHireMoney(r.expected_amount, cur),
        invoiced: formatHireMoney(r.invoiced_amount, cur),
        variance: formatHireMoney(r.variance_amount, cur),
      },
    });
  }

  return (
    <EngineCommercialA4Print
      docType="pmv_hire_recon"
      title="HIRE INVOICE RECONCILIATION"
      documentNumber={r.recon_number || 'HIR-REC'}
      partyName={supplierLabel || 'Supplier'}
      letterhead
      onClose={onClose}
      introText={
        'Neutral comparison of PMV expected hire statement vs referenced vendor invoice. ' +
        'PMV never posts Supplier Invoice, AP or GL. Resolution updates handoff status only.'
      }
      metaLines={[
        { label: 'Recon No', value: r.recon_number || '' },
        { label: 'Status', value: String(r.status || '') },
        { label: 'Agreement', value: agreementLabel || r.hire_agreement_id || '' },
        { label: 'Statement', value: statementLabel || r.statement_id || '' },
        { label: 'Equipment', value: equipmentLabel || '' },
        { label: 'Supplier', value: supplierLabel || '' },
        { label: 'Variance type', value: r.variance_type || '' },
        { label: 'Expected', value: formatHireMoney(r.expected_amount, cur) },
        { label: 'Invoiced', value: formatHireMoney(r.invoiced_amount, cur) },
        { label: 'Variance', value: formatHireMoney(r.variance_amount, cur) },
        { label: 'AP handoff', value: r.ap_handoff_status || 'NONE' },
      ].filter((m) => m.value)}
      columns={[
        { key: 'description', label: 'Line' },
        { key: 'expected', label: 'Expected', align: 'right' },
        { key: 'invoiced', label: 'Invoiced', align: 'right' },
        { key: 'variance', label: 'Variance', align: 'right' },
      ]}
      lines={lines}
      finals={[{ id: 'rec-note', heightPx: 48 }]}
      finalBlocks={{
        'rec-note': (
          <div style={{ fontSize: 10, color: '#444' }}>
            Note: {r.variance_note || '—'}
          </div>
        ),
      }}
    />
  );
};

export const PrintableDailyLogSheet: React.FC<
  DailyLogSheetPrintContext & { onClose?: () => void }
> = ({ sheet, entries, projectLabel, onClose }) => {
  const lines = entries.map((e, i) => ({
    lineNumber: e.line_no || i + 1,
    description: e.work_description || e.equipment_code || `Line ${e.line_no || i + 1}`,
    cells: {
      equip: e.equipment_code || '—',
      exp: e.expected_flag ? 'Y' : 'N',
      work: String(e.working_minutes ?? 0),
      idle: String(e.idle_minutes ?? 0),
      stby: String(e.standby_minutes ?? 0),
      bd: String(e.breakdown_minutes ?? 0),
      open: e.opening_meter_value == null ? '—' : String(e.opening_meter_value),
      close: e.closing_meter_value == null ? '—' : String(e.closing_meter_value),
    },
  }));
  if (!lines.length) {
    lines.push({
      lineNumber: 1,
      description: 'No entries on sheet',
      cells: {
        equip: '—',
        exp: '—',
        work: '—',
        idle: '—',
        stby: '—',
        bd: '—',
        open: '—',
        close: '—',
      },
    });
  }

  return (
    <EngineCommercialA4Print
      docType="pmv_daily_log_sheet"
      title="DAILY LOG SHEET"
      documentNumber={sheet.sheet_number || 'DLS'}
      partyName={projectLabel || 'Project'}
      letterhead
      landscape
      onClose={onClose}
      introText={
        'Bulk field operations envelope for site/shift capture. ' +
        'Individual Daily Logs remain operational truth after verify. ' +
        'This print is a worksheet / sign-off pack — not a commercial billing document.'
      }
      metaLines={[
        { label: 'Sheet No', value: sheet.sheet_number || '' },
        { label: 'Status', value: String(sheet.status || '') },
        { label: 'Operating date', value: fmtDate(sheet.operating_date) },
        { label: 'Shift', value: sheet.shift_code || '' },
        { label: 'Project', value: projectLabel || sheet.project_id || '' },
        { label: 'Site', value: sheet.site_reference || '' },
        { label: 'Entries', value: String(sheet.entry_count ?? entries.length) },
      ].filter((m) => m.value)}
      columns={[
        { key: 'equip', label: 'Equip' },
        { key: 'exp', label: 'Exp' },
        { key: 'description', label: 'Work' },
        { key: 'work', label: 'Work' },
        { key: 'idle', label: 'Idle' },
        { key: 'stby', label: 'Stby' },
        { key: 'bd', label: 'BD' },
        { key: 'open', label: 'Open' },
        { key: 'close', label: 'Close' },
      ]}
      lines={lines}
      finals={[{ id: 'dls-sign', heightPx: 72 }]}
      finalBlocks={{
        'dls-sign': (
          <div style={{ fontSize: 10, color: '#444' }}>
            Supervisor sign-off: ______________________ Date: __________ · Notes:{' '}
            {sheet.notes || '—'}
          </div>
        ),
      }}
    />
  );
};

export function openHireTimesheetPrint(ctx: HireTimesheetPrintContext): void {
  openPmvPrintOverlay((onClose) => <PrintableHireTimesheet {...ctx} onClose={onClose} />);
}

export function openHireStatementPrint(ctx: HireStatementPrintContext): void {
  openPmvPrintOverlay((onClose) => <PrintableHireStatement {...ctx} onClose={onClose} />);
}

export function openHireReconciliationPrint(ctx: HireReconPrintContext): void {
  openPmvPrintOverlay((onClose) => <PrintableHireReconciliation {...ctx} onClose={onClose} />);
}

export function openDailyLogSheetPrint(ctx: DailyLogSheetPrintContext): void {
  openPmvPrintOverlay((onClose) => <PrintableDailyLogSheet {...ctx} onClose={onClose} />);
}

export type PmvPassportPrintContext = {
  assetCode: string;
  assetName?: string;
  fleetNumber?: string;
  makeModel?: string;
  classification?: string;
  operationalStatus?: string;
  ownershipType?: string;
  criticality?: string;
  projectLabel?: string;
  meterLabel?: string;
  allocationLabel?: string;
  attention?: string[];
  readinessSummary?: string;
};

export const PrintablePmvPassport: React.FC<
  PmvPassportPrintContext & { onClose?: () => void }
> = ({
  assetCode,
  assetName,
  fleetNumber,
  makeModel,
  classification,
  operationalStatus,
  ownershipType,
  criticality,
  projectLabel,
  meterLabel,
  allocationLabel,
  attention,
  readinessSummary,
  onClose,
}) => {
  const docNo = fleetNumber || assetCode || 'PMV';
  const lines = [
    { key: 'status', label: 'Operational status', value: operationalStatus || '—' },
    { key: 'own', label: 'Ownership', value: ownershipType || '—' },
    { key: 'crit', label: 'Criticality', value: criticality || '—' },
    { key: 'class', label: 'Classification', value: classification || '—' },
    { key: 'project', label: 'Project / site', value: projectLabel || '—' },
    { key: 'alloc', label: 'Allocation', value: allocationLabel || '—' },
    { key: 'meter', label: 'Meter', value: meterLabel || '—' },
    { key: 'ready', label: 'Readiness', value: readinessSummary || '—' },
  ].map((r, i) => ({
    lineNumber: i + 1,
    description: r.label,
    cells: { value: r.value },
  }));

  return (
    <EngineCommercialA4Print
      docType="pmv_passport_summary"
      title="EQUIPMENT PASSPORT SUMMARY"
      documentNumber={docNo}
      partyName={assetName || makeModel || assetCode}
      letterhead
      onClose={onClose}
      introText={
        'One-page PMV equipment passport summary for site / operations handover. ' +
        'Operational status and allocation are live at print time. ' +
        'Does not replace Asset master, Maintenance case, or Hire agreement documents.'
      }
      metaLines={[
        { label: 'Asset code', value: assetCode || '' },
        { label: 'Fleet', value: fleetNumber || '' },
        { label: 'Name', value: assetName || '' },
        { label: 'Make / model', value: makeModel || '' },
        { label: 'Printed', value: dayjs().format('DD MMM YYYY HH:mm') },
      ].filter((m) => m.value)}
      columns={[
        { key: 'description', label: 'Field' },
        { key: 'value', label: 'Value' },
      ]}
      lines={lines}
      finals={[{ id: 'attn', heightPx: 64 }]}
      finalBlocks={{
        attn: (
          <div style={{ fontSize: 10, color: '#444' }}>
            <strong>Attention:</strong>{' '}
            {attention?.length ? attention.join(' · ') : 'No blockers at print time'}
          </div>
        ),
      }}
    />
  );
};

export function openPmvPassportPrint(ctx: PmvPassportPrintContext): void {
  openPmvPrintOverlay((onClose) => <PrintablePmvPassport {...ctx} onClose={onClose} />);
}
